import React, {useEffect, useState} from 'react';
import {Box, Divider, Tooltip} from '@mui/material';
import {DashboardPluginProps} from '../../../core/plugin/PluginType';
import Spinner from '../../../ui/Spinner';
import {useClockInOut} from '../../../hooks/useClockInOut';
import {PlayIcon, StopIcon} from '../../../components/Icons';
import Typography from '../../../components/Typography';
import {Chip} from "../../../components/Chip";
import {IconButton} from "../../../components/IconButton";
import {BaseTable, Column} from "../../../components/BaseTable";
import {TimeRecordEntry} from "../../../api/TimetrackingApi";
import {CollapsibleSection, CountBadge} from "../../../components/CollapsibleSection";
import {useWidgetActions} from "../../../core/components/useWidgetActions";
import {useTranslation} from "react-i18next";
import {Button} from "../../../components/Button";


/** Auto-collapse the entry list when there are more than this many rows. */
const AUTO_COLLAPSE_THRESHOLD = 5;

// ─── Formatting helpers ────────────────────────────────────────────────────

function formatDuration(totalSeconds: number): string {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function secondsSince(isoDate: string): number {
    return Math.max(0, Math.floor((Date.now() - new Date(isoDate).getTime()) / 1_000));
}

function formatTime(time: string): string {
    return time ? time.substring(0, 5) : '—';
}

function entryTypeLabel(type: string): string {
    switch (type) {
        case 'WORK':
            return 'Arbeit';
        case 'BREAK':
            return 'Pause';
        case 'ABSENCE':
            return 'Abwesenheit';
        default:
            return type;
    }
}

// ─── Live timer ────────────────────────────────────────────────────────────

interface LiveWorkedTimeProps {
    committedMinutes: number;
    committedSeconds: number;
    clockedInAt: string;
}

const LiveWorkedTime: React.FC<LiveWorkedTimeProps> = ({committedSeconds, clockedInAt}) => {

    const [elapsed, setElapsed] = useState(() => secondsSince(clockedInAt));

    useEffect(() => {
        const update = () => setElapsed(secondsSince(clockedInAt));
        update();
        const interval = setInterval(update, 1_000);
        return () => clearInterval(interval);
    }, [clockedInAt]);

    useEffect(() => {
        setElapsed(secondsSince(clockedInAt));
    }, [clockedInAt, committedSeconds]);

    return (
        <Typography variant="h5" color="primary">
            {formatDuration(committedSeconds + elapsed)}
        </Typography>
    );
};

// ─── Table columns ─────────────────────────────────────────────────────────

const entryColumns: Column<TimeRecordEntry>[] = [
    {
        key: 'entryType',
        label: 'Typ',
        sortable: false,
        render: (value) => entryTypeLabel(value),
    },
    {
        key: 'localStartTime',
        label: 'Von',
        sortable: false,
        render: (value) => formatTime(value),
    },
    {
        key: 'localEndTime',
        label: 'Bis',
        sortable: false,
        render: (value, row) => row.open ? '—' : formatTime(value),
    },
    {
        key: 'startTimestamp',
        label: 'Gesamt',
        sortable: false,
        align: 'right',
        render: (_value, row) => {
            const seconds = row.durationSeconds ?? null;
            return seconds !== null ? formatDuration(seconds) : '—';
        },
    },
];

// ─── Widget ────────────────────────────────────────────────────────────────

const CurrentWorkTimeWidget: React.FC<DashboardPluginProps> = ({context, size}) => {

    const {t} = useTranslation();

    const {
        workDayRecord,
        workedMinutes,
        workedSeconds,
        error,
        loading,
        clockedInAt,
        isClockedIn,
        isClockingIn,
        isClockingOut,
        clockIn,
        clockOut,
    } = useClockInOut();

    useWidgetActions(
        () => (
            <Chip
                label={isClockedIn ? 'Eingestempelt' : 'Ausgestempelt'}
                color={isClockedIn ? 'success' : 'warning'}
                size="small"
            />
        ),
        [isClockedIn, t],
    );

    if (loading) {
        return <Spinner/>;
    }

    const isNoRecordToday = error?.code === 'NO_TIME_RECORD_FOR_TODAY';
    const hasRealError = error && !isNoRecordToday;
    const timeRecord = workDayRecord?.timeRecord

    const entries: TimeRecordEntry[] = timeRecord?.entries ?? [];

    // Automatically start collapsed when the list is long so the widget
    // doesn't overflow. The user can always expand it manually.
    const defaultEntriesExpanded = entries.length <= AUTO_COLLAPSE_THRESHOLD;

    return (
        <Box display="flex" flexDirection="column" gap={2} height="100%">
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={1}
                gap={0.5}
            >
                {hasRealError ? (
                    error?.code === 'NOT_ALLOWED_WORKING_DAY' && (
                        <Typography variant="body2" color="error" align="center">
                            Du kannst dich heute gemäß deiner Arbeitszeitregelung nicht einstempeln.
                        </Typography>
                    )
                ) : (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                    >
                        <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                            {isClockedIn && clockedInAt ? (
                                <LiveWorkedTime
                                    committedMinutes={workedMinutes}
                                    committedSeconds={workedSeconds}
                                    clockedInAt={clockedInAt}
                                />
                            ) : (
                                <Typography variant="h5" color="primary">
                                    {formatDuration(workedSeconds)}
                                </Typography>
                            )}

                            <Typography variant={'caption'} color="secondary">
                                Heutige Arbeitszeit
                            </Typography>
                        </Box>

                        <Box py={1.5} px={4}>
                            <Tooltip title={isClockedIn ? 'Ausstempeln' : 'Einstempeln'} placement="top">
                                  <span>
                                    <IconButton
                                        size="large"
                                        variant="contained"
                                        onClick={isClockedIn ? () => clockOut() : () => clockIn()}
                                        isDisabled={isClockingIn || isClockingOut}
                                        color={isClockedIn ? 'error' : 'success'}
                                        loading={isClockingIn || isClockingOut}
                                        aria-label={isClockedIn ? 'Ausstempeln' : 'Einstempeln'}
                                        className="rounded-2xl"
                                    >
                                        {isClockedIn ? (
                                            <StopIcon className="text-white"/>
                                        ) : (
                                            <PlayIcon className="text-white"/>
                                        )}
                                    </IconButton>
                                  </span>
                            </Tooltip>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* ── Entry list with auto-collapsible wrapper ─────────────── */}
            {!hasRealError && entries.length > 0 && (
                <>
                    <Divider/>
                    <CollapsibleSection
                        title="Einträge"
                        defaultExpanded={defaultEntriesExpanded}
                        badge={<CountBadge count={entries.length}/>}
                        contentClassName="pt-1"
                    >
                        <BaseTable<TimeRecordEntry>
                            data={entries}
                            columns={entryColumns}
                            rowKey={(row) => row.id}
                        />
                    </CollapsibleSection>
                </>
            )}
        </Box>
    );
};

export default CurrentWorkTimeWidget;